import { getNews, deleteNews, updateNews, addNews } from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { INews, INewsRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
  Button,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';

const _modal: IDataModal<INews> = {
  open: false,
};

const NewsPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<INews[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<INews[]>([]);
  const [modal, setModal] = useState<IDataModal<INews>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<INewsRes, Error>({
    queryKey: ['get-news'],
    queryFn: getNews,
    retry: 0,
  });

  const actionNewsMutation = useMutation({
    mutationFn: async (data: Partial<INews>) =>
      tab === 'create' ? await addNews(data) : await updateNews(data),
    mutationKey: tab === 'create' ? ['add-News'] : ['update-News'],
    onSuccess: data => {
      if (data.status === 'fulfilled' && tab === 'create') {
        notification.success({ message: 'Sync News สำเร็จ' });
        refetch();
      } else if (data.status === 'fulfilled' && tab === 'update') {
        notification.success({ message: 'Update News สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: data.reason?.errors?.errorMessage });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedNews = async (selectedRows: any) => {
    await actionNewsMutation.mutateAsync(selectedRows);
  };

  const actionDeleteNews = useMutation({
    mutationFn: async (data: Partial<INews>) => await deleteNews(data),
    mutationKey: ['delete-News'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete News สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete News ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeleteNews = async (selectedRows: any) => {
    await actionDeleteNews.mutateAsync(selectedRows);
  };

  const handleUpdateNewsCode = useCallback((item: INews) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: INews, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'เอกสาร',
          dataIndex: 'NEWS_CODE',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.NEWS_CODE?.replace(/\D/g, '').replace('-', '')) -
            Number(b.NEWS_CODE?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.NEWS_CODE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.NEWS_CODE?.startsWith(value as string);
          },
        },
        {
          title: 'วันที่',
          dataIndex: 'NEWS_DATE',
          render: NEWS_DATE => `${dayjs(NEWS_DATE).format('DD/MM/YYYY')}`,
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected.map(item => {
                return dayjs(item.NEWS_DATE).format('DD/MM/YYYY');
              }),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return dayjs(record?.NEWS_DATE).format('DD/MM/YYYY') === value;
          },
        },
        {
          title: 'ประเภทข่าว',
          dataIndex: 'NEWS_TYPE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.NEWS_TYPE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.NEWS_TYPE?.startsWith(value as string);
          },
        },
        {
          title: 'หัวข้อข่าว',
          dataIndex: 'NEWS_HLINE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.NEWS_HLINE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return value
              ? record?.NEWS_HLINE?.startsWith(value as string)
              : !record?.NEWS_HLINE;
          },
        },
        {
          title: 'เนื้อหาข่าวสาร',
          dataIndex: 'NEWS_CONTENT',
        },
      ] as TableColumnsType<INews>,
    [tab, selected, handleUpdateNewsCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: INews[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: INews) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const newsCode = (item.NEWS_CODE || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || newsCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeleteNews(selectedRows)}
            >
              {actionDeleteNews.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล News'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedNews(selectedRows)}
          >
            {actionNewsMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<INews>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="land"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<INews>
              label="NEWS CODE"
              name="NEWS_CODE"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(news => ({
                  label: `${news.NEWS_CODE} - ${news.NEWS_CODE}`,
                  value: news.NEWS_CODE,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default NewsPage;
